// app/routes/_authed/_professional/professional/billing.tsx
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { CreditCard, ExternalLink } from "lucide-react"
import { Page } from "@/components/page"
import { PageLoader } from "@/components/page-loader"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"
import { getRegionDetails } from "@/server/region"
import { formatCurrencyByRegion } from "@/utils/helpers"
import { validateRegionDetails } from "@/utils/region"

export const Route = createFileRoute("/_authed/_professional/professional/billing")({
  component: BillingRoute,
})

export function BillingRoute() {
  const { data: user, isLoading: userLoading } = trpc.users.me.useQuery()
  const isProfessional = user?.userType === "professional"
  const portalSession = trpc.stripe.createPortalSession.useMutation({
    onSuccess: ({ url }) => {
      // Stripe-hosted portal
      window.location.assign(url)
    },
  })
  const handleOpenBillingPortal = () => {
    // Stripe requires a full URL
    const returnUrl = window.location.href
    portalSession.mutate({ returnUrl })
  }

  const { data: subscription, isLoading: subscriptionLoading } =
    trpc.stripe.getSubscriptionStatus.useQuery(undefined, {
      enabled: !!isProfessional,
    })

  const getRegion = useServerFn(getRegionDetails)
  const { data: regionDetails } = useQuery({
    queryKey: ["regionDetails"],
    queryFn: () => getRegion(),
    enabled: isProfessional,
  })
  const region = validateRegionDetails(regionDetails)

  if (userLoading || subscriptionLoading) {
    return (
      <Page
        headerSlot={
          <Typography as="h1" size="2xl">
            Billing
          </Typography>
        }
      >
        <PageLoader />
      </Page>
    )
  }

  if (!isProfessional) {
    return (
      <Page
        headerSlot={
          <Typography as="h1" size="2xl">
            Billing
          </Typography>
        }
      >
        <Alert variant="destructive">
          <AlertDescription>Billing is only available for professional accounts.</AlertDescription>
        </Alert>
      </Page>
    )
  }

  const hasSubscription = !!subscription
  const subscriptionStatus = subscription?.status ?? "inactive"
  const hasActiveSubscription = subscriptionStatus === "active" || subscriptionStatus === "trialing"

  const trialEndDate = subscription?.trialEnd ? new Date(subscription.trialEnd) : null
  const nextBillingDate = subscription?.nextBillingDate
    ? new Date(subscription.nextBillingDate)
    : null

  const planName = subscription?.plan ?? "No active plan"

  let billingCycleLabel = "—"

  if (subscription?.billingCycle === "year") {
    billingCycleLabel = "Annual"
  } else if (subscription?.billingCycle === "month") {
    billingCycleLabel = "Monthly"
  }

  const billingAmountLabel =
    subscription?.cost != null ? formatCurrencyByRegion(subscription.cost, region) : "—"

  const getLocaleByRegion = (region: "us" | "ca" | undefined) => {
    switch (region) {
      case "ca":
        return "en-CA"
      case "us":
        return "en-US"
      default:
        return "en-US"
    }
  }
  const dateLocale = getLocaleByRegion(region)

  const nextBillingDateLabel = nextBillingDate
    ? nextBillingDate.toLocaleDateString(dateLocale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—"

  const statusBadgeLabel = hasSubscription ? subscriptionStatus : "Inactive"

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Billing
        </Typography>
      }
    >
      <div className="space-y-8">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="col-span-1 xl:col-span-9">
            {/* CURRENT SUBSCRIPTION */}
            <Card className="border">
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Typography as="h2" size="xl" className="font-semibold">
                      Current Subscription
                    </Typography>
                    <Typography as="p" size="sm" className="text-muted-foreground mt-1">
                      View your current plan and billing status.
                    </Typography>
                  </div>

                  <Badge
                    variant={hasActiveSubscription ? "success" : "destructive"}
                    className="mt-1"
                    status
                  >
                    {statusBadgeLabel}
                  </Badge>
                </div>

                {hasSubscription ? (
                  <>
                    <div className="grid gap-4 md:grid-cols-4">
                      <div className="rounded-lg border bg-muted/40 p-4 flex flex-col">
                        <span className="text-xs font-medium text-muted-foreground">PLAN</span>
                        <span className="mt-2 text-sm font-semibold">{planName}</span>
                      </div>

                      <div className="rounded-lg border bg-muted/40 p-4 flex flex-col">
                        <span className="text-xs font-medium text-muted-foreground">
                          BILLING CYCLE
                        </span>
                        <span className="mt-2 text-sm font-semibold">{billingCycleLabel}</span>
                      </div>

                      <div className="rounded-lg border bg-muted/40 p-4 flex flex-col">
                        <span className="text-xs font-medium text-muted-foreground">
                          BILLING AMOUNT
                        </span>
                        <span className="mt-2 text-sm font-semibold">{billingAmountLabel}</span>
                      </div>

                      <div className="rounded-lg border bg-muted/40 p-4 flex flex-col">
                        <span className="text-xs font-medium text-muted-foreground">
                          NEXT BILLING / RENEWAL
                        </span>
                        <span className="mt-2 text-sm font-semibold">
                          {subscriptionStatus === "trialing" && trialEndDate
                            ? trialEndDate.toLocaleDateString(dateLocale, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : nextBillingDateLabel}
                        </span>

                        {subscriptionStatus === "trialing" && trialEndDate && (
                          <span className="mt-1 text-[11px] text-muted-foreground">
                            You are currently in a trial. This date represents when your trial ends.
                          </span>
                        )}

                        {subscription?.cancelAtPeriodEnd && (
                          <span className="mt-1 text-[11px] text-muted-foreground">
                            Your subscription is set to cancel at the end of the current billing
                            period.
                          </span>
                        )}
                      </div>
                    </div>

                    <Alert variant="information">
                      <AlertDescription className="text-xs md:text-sm text-information-foreground">
                        <span className="flex space-x-2 items-center">
                          <CreditCard className="h-4 w-4" />
                          <span className="font-semibold">Billing information</span>
                        </span>
                        Your card may be billed a subscription fee as well as predefined prices for
                        individual reports that are generated. Report costs vary based on complexity
                        and are charged separately as they&apos;re created.
                      </AlertDescription>
                    </Alert>
                  </>
                ) : (
                  <Alert>
                    <AlertDescription>
                      You don&apos;t currently have an active professional subscription. Once you
                      subscribe, your plan details and invoices will appear here.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="col-span-1 xl:col-span-3">
            {/* BILLING MANAGEMENT (Stripe Customer Portal) */}
            <Card className="border">
              <CardContent className="space-y-4">
                <div className="flex flex-col items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Typography as="h2" size="lg" className="font-semibold">
                      Manage Billing
                    </Typography>
                    <Typography as="p" size="sm" className="text-muted-foreground">
                      Update payment methods, billing details, invoices, and subscription settings
                      in Stripe.
                    </Typography>
                  </div>
                  {hasSubscription && (
                    <Button
                      className="gap-2 w-full"
                      onClick={handleOpenBillingPortal}
                      disabled={!hasSubscription || portalSession.isPending}
                    >
                      <ExternalLink className="h-4 w-4" />
                      {portalSession.isPending ? "Opening…" : "Manage billing"}
                    </Button>
                  )}
                </div>

                {!hasSubscription && (
                  <Alert>
                    <AlertDescription className="text-xs md:text-sm">
                      You&apos;ll be able to access the billing portal once you&apos;ve started a
                      subscription and a payment method is on file.
                    </AlertDescription>
                  </Alert>
                )}

                {portalSession.error && (
                  <Alert variant="destructive">
                    <AlertDescription className="text-xs md:text-sm">
                      {portalSession.error.message}
                    </AlertDescription>
                  </Alert>
                )}

                {hasSubscription && (
                  <Alert>
                    <AlertDescription className="text-xs md:text-sm">
                      Stripe securely hosts this page. You&apos;ll be returned here after
                      you&apos;re done.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Page>
  )
}
